// ===========================================================================
// ReHome — Smart Match session persistence
// ---------------------------------------------------------------------------
// A "Smart Match" kitöltése 5 kérdés — komoly befektetés a felhasználó
// részéről. Korábban ez az eredmény kizárólag a GamifiedMatcherPage React
// state-jében élt, így BÁRMILYEN teljes oldal-újratöltés eldobta: a
// leggyakoribb eset az volt, hogy a be nem jelentkezett user rákattintott
// egy találatra, a login fal átdobta a /login-ra, és onnan visszatérve a
// matching végleg elveszett — újra kellett kattintgatnia mind az 5 kérdést.
//
// A megoldás a "lazy registration" mintát követi (NN/G – Login Walls,
// Baymard – Delayed Account Creation): a felhasználó regisztráció NÉLKÜL
// futtathatja a matchinget és láthatja a TELJES eredményt; a fiók csak az
// örökbefogadási igény leadásánál (a tranzakciós ponton) kell. Ahhoz, hogy
// ez működjön, az eredménynek túl kell élnie a bejelentkezési kitérőt —
// ezt tárolja el ez a modul.
//
// Tárolás: `localStorage`, nem `sessionStorage`. A sessionStorage túlélné a
// sima login-átirányítást, de NEM éli túl azt, amikor a user az emailben
// kapott megerősítő linket egy ÚJ fülön nyitja meg — pedig pont a frissen
// regisztrálók futnak bele ebbe. A TTL korlátozza, meddig maradjon meg.
//
// Csak azonosítókat + pontszámokat tárolunk (állat-ID, score, indoklás), a
// teljes állatrekordot soha: az állat adata (státusz, kép, elérhetőség)
// változhat, ezért visszatöltéskor mindig frissen kérdezzük le a DB-ből, és
// a köztben elkelt állatok kiesnek. Így a snapshot nem "áporodik".
// ===========================================================================

import type { LifestyleProfile } from './types';

/** localStorage kulcs. A `v1` suffix teszi lehetővé a séma későbbi váltását. */
const STORAGE_KEY = 'rehome.matchSession.v1';

/**
 * Meddig érvényes egy elmentett matching. 7 nap: elég hosszú ahhoz, hogy egy
 * email-megerősítéses regisztrációt (ami órákig-napokig is elhúzódhat) túléljen,
 * de elég rövid ahhoz, hogy egy fél éve futtatott matching ne ugorjon vissza
 * kéretlenül egy rég elfelejtett életmód-profillal.
 */
const TTL_MS = 7 * 24 * 60 * 60 * 1000;

/** Egy találat a matching eredményéből. */
export interface MatchResultEntry {
  animalId: string;
  score: number;
  reason: string;
}

/** Egy teljes, elmentett matching futás. */
export interface MatchSession {
  /** A kitöltött életmód-profil — ez teszi lehetővé az "Újrafuttatás"-t. */
  profile: LifestyleProfile;
  /** A találatok, sorrendben (legjobb elöl). */
  results: MatchResultEntry[];
  /** Mentés időpontja (ISO) — a TTL és a "mikor futtattad" felirat alapja. */
  savedAt: string;
}

/**
 * localStorage elérése soha nem dobhat a hívó felé: privát böngészőablakban,
 * letiltott site-adatoknál vagy tele kvótánál a hozzáférés maga is kivételt
 * dob. Ilyenkor a matching egyszerűen nem marad meg — a funkció working
 * marad, csak a perzisztencia esik ki.
 */
const safeStorage = (): Storage | null => {
  try {
    return typeof window !== 'undefined' ? window.localStorage : null;
  } catch {
    return null;
  }
};

/** Elmenti a matching futás eredményét. Hiba esetén némán nem csinál semmit. */
export function saveMatchSession(profile: LifestyleProfile, results: MatchResultEntry[]): void {
  const store = safeStorage();
  if (!store) return;
  try {
    const session: MatchSession = { profile, results, savedAt: new Date().toISOString() };
    store.setItem(STORAGE_KEY, JSON.stringify(session));
  } catch {
    /* kvóta / privát mód — a matching enélkül is működik */
  }
}

/**
 * Visszatölti az utolsó matching futást, vagy `null`-t ad, ha nincs, lejárt,
 * vagy sérült. Lejárt/sérült bejegyzést egyből ki is takarít, hogy ne
 * maradjon szemét a tárolóban.
 */
export function loadMatchSession(): MatchSession | null {
  const store = safeStorage();
  if (!store) return null;
  let raw: string | null;
  try {
    raw = store.getItem(STORAGE_KEY);
  } catch {
    return null;
  }
  if (!raw) return null;

  try {
    const parsed = JSON.parse(raw) as MatchSession;
    // Alakhelyesség: kézzel szerkesztett vagy régi formátumú érték is ideérhet.
    if (!parsed || !Array.isArray(parsed.results) || !parsed.profile || !parsed.savedAt) {
      clearMatchSession();
      return null;
    }
    if (Date.now() - new Date(parsed.savedAt).getTime() > TTL_MS) {
      clearMatchSession();
      return null;
    }
    return parsed;
  } catch {
    clearMatchSession();
    return null;
  }
}

/** Törli az elmentett matchinget (pl. "Újrakezdés" gombra). */
export function clearMatchSession(): void {
  const store = safeStorage();
  if (!store) return;
  try {
    store.removeItem(STORAGE_KEY);
  } catch {
    /* nincs teendő */
  }
}

/**
 * Van-e visszatölthető matching? A layout ez alapján tud "Vissza a
 * találataidhoz" belépési pontot mutatni anélkül, hogy az egész sessiont
 * beolvasná és deszerializálná minden rendereléskor.
 */
export function hasMatchSession(): boolean {
  return loadMatchSession() !== null;
}
